import { useState, useMemo, useCallback, useEffect } from "react";

export const useSalaryComponents = (
  type,
  componentData,
  totalSalary = 0,
  isCompanyChanged = false,
) => {
  const [components, setComponents] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isCompanyChanged) {
      setComponents([]);
      setErrors({});
    }
  }, [componentData, isCompanyChanged]);

  useEffect(() => {
    if (
      type === "earnings" &&
      components.length === 0 &&
      componentData?.length !== 0 &&
      componentData?.data?.length !== 0
    ) {
      const specialAllowanceData = componentData?.data.find(
        (item) => item.code === "SP",
      );

      const basicData = componentData?.data.find(
        (item) => item.code?.toUpperCase() === "BASIC",
      );

      if (!specialAllowanceData || !basicData) return;

      const specialAllowance = {
        id: specialAllowanceData?.id,
        component_name: {
          label: specialAllowanceData?.name,
          id: specialAllowanceData?.id,
        },
        component_category: specialAllowanceData?.component_category,
        computation_method: specialAllowanceData?.computation_method,
        value_based_formula: specialAllowanceData?.value,
        amount: totalSalary || 0,
        code: specialAllowanceData?.code,
        isSpecialAllowance: true,
      };
      const basicComponent = {
        id: basicData?.id,
        component_name: {
          label: basicData?.name,
          id: basicData?.id,
        },
        component_category: basicData?.component_category,
        computation_method: basicData?.computation_method,
        computation_value: basicData?.value,
        value_based_on: basicData?.value_based_on,
        amount: basicData?.amount,
        code: basicData?.code,
        isBasic: true,
        value_based_percentage: `${basicData?.value}% of ${basicData?.value_based_on}`,
      };
      setComponents([specialAllowance, basicComponent]);
    }
  }, [
    type,
    totalSalary,
    components.length,
    componentData?.length,
    componentData,
  ]);

  const validateComponents = () => {
    const newErrors = {};
    let isValid = true;

    components.forEach((component, index) => {
      if (!component.component_name || !component.component_name.label) {
        newErrors[index] = {
          ...newErrors[index],
          component_name: "Value is required",
        };
        isValid = false;
      }

      if (!component.computation_method) {
        newErrors[index] = {
          ...newErrors[index],
          computation_method: "Value is required",
        };
        isValid = false;
      }

      if (component.isSpecialAllowance) {
        if (component.status && component.status.includes("Deficit")) {
          newErrors[index] = {
            ...newErrors[index],
            amount: "cannot be negative",
          };
          isValid = false;
        }
      } else {
        if (
          !component.amount ||
          component.amount === 0 ||
          component.amount === "" ||
          component.amount === "0"
        ) {
          newErrors[index] = {
            ...newErrors[index],
            amount: "Value is required",
          };
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const buildSpecialAllowanceFormula = (components) => {
    const deductionCodes = components
      .filter((comp) => {
        return (
          !comp.isSpecialAllowance &&
          comp.code &&
          comp.amount !== undefined &&
          comp.amount !== null &&
          comp.amount !== ""
        );
      })
      .map((comp) => comp.code);

    return deductionCodes.length
      ? `GROSS - ${[...deductionCodes].join(" - ")}`
      : "GROSS";
  };

  const calculateSpecialAllowance = useCallback(
    (allComponents) => {
      const specialAllowanceIndex = allComponents.findIndex(
        (comp) => comp.isSpecialAllowance,
      );
      const formula = buildSpecialAllowanceFormula(allComponents);
      if (specialAllowanceIndex !== -1) {
        const totalOtherEarnings = allComponents.reduce((sum, comp, index) => {
          if (index !== specialAllowanceIndex && comp.amount) {
            return sum + parseFloat(comp.amount);
          }
          return sum;
        }, 0);

        const updatedComponents = [...allComponents];
        const difference = totalSalary - totalOtherEarnings;
        const newSpecialAllowanceAmount = Math.max(0, difference);

        updatedComponents[specialAllowanceIndex] = {
          ...updatedComponents[specialAllowanceIndex],
          amount: newSpecialAllowanceAmount,
          value_based_formula: formula,

          status:
            difference >= 0
              ? difference === 0
                ? "Zero - All salary allocated"
                : `Balance: ${difference.toFixed(2)}`
              : `Deficit: ${Math.abs(difference).toFixed(2)} (capped at 0)`,
        };

        if (difference <= 0) {
          updatedComponents[specialAllowanceIndex].hasWarning = true;
          updatedComponents[specialAllowanceIndex].originalDeficit =
            Math.abs(difference);
        }

        return updatedComponents;
      }

      return allComponents;
    },
    [totalSalary],
  );

  const handleComponentNameChange = (index, value, setComponents) => {
    const updatedComponents = [...components];
    const selectedComponent = componentData?.data?.find(
      (obj) => obj.name === value?.label,
    );

    if (selectedComponent) {
      updatedComponents[index] = {
        ...updatedComponents[index],
        component_name: value,
        computation_method: selectedComponent.computation_method,
        component_category: selectedComponent?.component_category,
        computation_value: selectedComponent.value,
        code: selectedComponent.code,
        value_based_on: selectedComponent.value_based_on,
        amount:
          selectedComponent.computation_method === "Fixed Amount"
            ? selectedComponent.value
            : 0,
        ...(selectedComponent.computation_method === "Percentage" && {
          value_based_percentage: `${selectedComponent.value}% of ${selectedComponent.value_based_on}`,
        }),
        ...(selectedComponent.computation_method === "Formula" && {
          value_based_formula: selectedComponent.value,
        }),
        isSpecialAllowance: value?.label === "Special Allowance",
      };
    } else {
      updatedComponents[index] = {
        ...updatedComponents[index],
        component_name: value,
        isSpecialAllowance: value?.label === "Special Allowance",
      };
    }

    const finalComponents = calculateSpecialAllowance(updatedComponents);
    setErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], component_name: undefined },
    }));

    setComponents(finalComponents);
  };

  const handleComputationMethodChange = (index, value, setComponents) => {
    const updatedComponents = [...components];
    updatedComponents[index].computation_method = value;

    if (
      updatedComponents[index].isSpecialAllowance &&
      value !== "Fixed Amount"
    ) {
      updatedComponents[index].computation_method = "Fixed Amount";
      return;
    }
    if (!updatedComponents[index].isSpecialAllowance) {
      const finalComponents = calculateSpecialAllowance(updatedComponents);
      setComponents(finalComponents);
    } else {
      setComponents(updatedComponents);
    }
    setErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], computation_method: undefined },
    }));
  };

  const handleAmountChange = (index, event, setComponents) => {
    const value = event.target.value;
    if (value?.length <= 8 && /^\d*\.?\d*$/.test(value)) {
      const updatedComponents = [...components];
      if (updatedComponents[index].isSpecialAllowance) {
        return;
      }

      updatedComponents[index].amount = value;
      const finalComponents = calculateSpecialAllowance(updatedComponents);
      setErrors((prev) => ({
        ...prev,
        [index]: { ...prev[index], amount: undefined },
      }));

      setComponents(finalComponents);
    }
  };

  const addComponent = (validateFirst = true) => {
    if (validateFirst) {
      const isValid = validateComponents();
      if (!isValid) {
        return;
      }
    }

    const newId = `${type}_${Date.now()}_${components.length}`;
    const newComponent = {
      id: newId,
      component_name: "",
      computation_method: "",
      computation_value: "",
      amount: 0,
      isSpecialAllowance: false,
    };

    const updatedComponents = [...components, newComponent];
    const finalComponents = calculateSpecialAllowance(updatedComponents);
    setComponents(finalComponents);
  };

  const filteredComponentLists = useMemo(() => {
    if (!componentData?.data) return [];

    return components.map((_, index) => {
      const selectedComponents = new Set();

      components.forEach((component, i) => {
        if (i !== index && component.component_name?.label) {
          selectedComponents.add(component.component_name.label);
        }
      });

      return componentData.data
        .filter((obj) => !selectedComponents.has(obj.name))
        .map((obj) => ({
          label: obj.name,
          id: obj.id,
        }));
    });
  }, [componentData?.data, components]);

  const totalAmount = useMemo(
    () =>
      components.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
    [components],
  );

  const prepareSubmissionData = () => {
    return components.map((component) => ({
      component_id: component.component_name?.id || component.id,
      component_category: component?.component_category,
      computation_method: component.computation_method,
      computation_value:
        component?.computation_method === "Fixed Amount"
          ? component?.amount
          : component?.computation_method === "Formula"
            ? component?.value_based_formula
            : component?.value_based_percentage,
      amount: component.amount || 0,
    }));
  };

  return {
    components,
    setComponents,
    errors,
    setErrors,
    filteredComponentLists,
    totalAmount,
    validateComponents,
    handleComponentNameChange,
    handleComputationMethodChange,
    handleAmountChange,
    addComponent,
    // removeComponent,
    prepareSubmissionData,
  };
};
